import type { ReactNode } from 'react';

/**
 * Shared screen furniture. Extracted because Bag/Book/Menu/Shop had each grown
 * their own copy of the same stat row and panel, and a palette change would have
 * needed four edits.
 */

export function Panel({ title, children, className = '' }: {
  title?: string; children: ReactNode; className?: string;
}) {
  return (
    <section className={`rounded-md border border-[var(--hairline)] bg-[var(--surface)] p-3.5 ${className}`}>
      {title && <h2 className="mb-2.5 font-pixel text-[9px] tracking-wide text-torch-core">{title}</h2>}
      {children}
    </section>
  );
}

export function Stat({ k, v, tone }: {
  k: string; v: ReactNode; tone?: 'coin' | 'kyat' | 'ok' | 'danger';
}) {
  const colour =
    tone === 'coin' ? 'text-coin'
    : tone === 'kyat' ? 'text-kyat-glow'
    : tone === 'ok' ? 'text-[var(--ok)]'
    : tone === 'danger' ? 'text-[var(--danger)]'
    : 'text-ink-hi';
  return (
    <div className="flex items-baseline justify-between gap-1.5 rounded-lg bg-black/30 px-2.5 py-2 text-[11px]">
      <span className="text-ink-dim">{k}</span>
      <b className={`tabular ${colour}`}>{v}</b>
    </div>
  );
}

export function StatGrid({ children }: { children: ReactNode }) {
  return <div className="grid grid-cols-2 gap-1.5">{children}</div>;
}

export function Button({ children, onClick, variant = 'default', disabled, busy, className = '' }: {
  children: ReactNode;
  onClick?: () => void;
  variant?: 'default' | 'primary' | 'ad' | 'danger';
  disabled?: boolean;
  busy?: boolean;
  className?: string;
}) {
  const styles = {
    default: 'border-ui-lit bg-[linear-gradient(#3A2718,var(--cave-floor))] text-ink-hi',
    primary: 'border-[rgba(255,220,170,.5)] bg-[linear-gradient(#FFA945,#E0761C)] text-[#2A1503] shadow-[0_4px_0_-1px_#8C440C]',
    ad: 'border-[rgba(169,107,240,.35)] bg-[linear-gradient(#6B45A8,#3A2560)] text-kyat-glow',
    danger: 'border-[rgba(224,85,63,.4)] bg-black/30 text-[var(--danger)]',
  }[variant];

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled || busy}
      className={`rounded-sm border px-3 py-2.5 text-sm font-bold transition-transform
                  active:translate-y-0.5 disabled:opacity-40 disabled:grayscale ${styles} ${className}`}
    >
      {busy ? '…' : children}
    </button>
  );
}

/** Back-arrow header for any screen reached from the Functions list. */
export function ScreenHeader({ icon, title, onBack }: {
  /** A drawn <Icon>, or a string for the screens not yet converted. */
  icon: ReactNode; title: string; onBack: () => void;
}) {
  return (
    <header className="mb-3 flex items-center gap-3">
      <button
        type="button"
        onClick={onBack}
        aria-label="Back"
        className="rounded-sm border border-ui-lit bg-[linear-gradient(#3A2718,var(--cave-floor))] px-3 py-2"
      >
        ◀
      </button>
      <span className="grid h-11 w-11 place-items-center rounded-xl border border-ui-lit
                       bg-[linear-gradient(#3B2917,#1D1309)] text-xl" aria-hidden>{icon}</span>
      <h1 className="font-extrabold">{title}</h1>
    </header>
  );
}

export function Row({ icon, title, subtitle, right, onClick }: {
  /** A drawn <Icon>, or a string for the screens not yet converted. */
  icon: ReactNode; title: string; subtitle?: string;
  right?: ReactNode; onClick?: () => void;
}) {
  const Tag = onClick ? 'button' : 'div';
  return (
    <Tag
      {...(onClick ? { type: 'button' as const, onClick } : {})}
      className="flex w-full items-center gap-3 border-t border-[var(--hairline)] py-3 text-left first:border-t-0"
    >
      <span className="grid h-10 w-10 shrink-0 place-items-center rounded-xl border
                       border-[var(--hairline)] bg-black/35 text-lg" aria-hidden>{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-[13px] font-bold">{title}</span>
        {subtitle && <span className="mt-0.5 block text-[11px] text-ink-dim">{subtitle}</span>}
      </span>
      {right}
    </Tag>
  );
}

export function Meter({ pct, tone = 'green', label = 'Progress' }: {
  pct: number; tone?: 'green' | 'gold' | 'kyat' | 'xp';
  /** What this bar is measuring — Withdraw/Missions/Achievements/Profile each show
      a different one. Defaults to a generic name rather than requiring every
      existing caller to change, but a specific one reads far better. */
  label?: string;
}) {
  const fill = {
    green: 'linear-gradient(90deg,#5CA83E,#8FD96A)',
    gold: 'linear-gradient(90deg,#F5A02A,#FFD36E)',
    kyat: 'linear-gradient(90deg,#6FE3D4,#2E5C57)',
    xp: 'linear-gradient(90deg,#5AB7FF,#B77BFF)',
  }[tone];
  const clamped = Math.max(0, Math.min(100, pct));
  return (
    <span
      role="progressbar"
      aria-valuenow={Math.round(clamped)}
      aria-valuemin={0}
      aria-valuemax={100}
      aria-label={label}
      className="block h-2 overflow-hidden rounded-full bg-white/10"
    >
      <i
        className="block h-full rounded-full transition-[width] duration-300"
        style={{ width: `${clamped}%`, background: fill }}
      />
    </span>
  );
}

export function Loading() {
  return <p className="py-8 text-center text-[11px] text-ink-dim">Loading…</p>;
}

export function ErrorNote({ children }: { children: ReactNode }) {
  return (
    <p role="alert" className="my-2 rounded-lg border border-[rgba(224,85,63,.4)]
                               bg-[rgba(224,85,63,.1)] px-3 py-2 text-xs text-[var(--danger)]">
      {children}
    </p>
  );
}

export function Empty({ children }: { children: ReactNode }) {
  return <p className="py-8 text-center text-[11px] text-ink-dim">{children}</p>;
}

/** Screen scroller with consistent padding and safe-area handling. */
export function ScreenBody({ children }: { children: ReactNode }) {
  return (
    <div className="h-full overflow-y-auto px-3 pb-[calc(env(safe-area-inset-bottom)+24px)] pt-3.5">
      {children}
    </div>
  );
}
